import React, {useReducer} from 'react';
import {createPortal} from 'react-dom';

import type {IndicatorRegion} from './AnnotationModeStore';
import {useAnnotationModeStore} from './AnnotationModeStore';
import AnnotationNoteIndicator from './AnnotationNoteIndicator';
import {resolveIndicatorScroller} from './annotationIndicatorPlacement';
import type {AnnotationNoteLine, MeasuredNote} from './annotationNoteLines';
import {groupNotesByLine, measureNotes} from './annotationNoteLines';
import {filterNotedAnnotations} from './annotationSelectors';
import type {Annotation} from './annotationTypes';
import useOnRegionsShift from './useOnRegionsShift';

interface IndicatorPane {
  scroller: HTMLElement;
  lines: AnnotationNoteLine[];
}

/** Each pane's noted marks merged into lines, one entry per pane that flags any. */
function measureIndicatorPanes(
  regions: readonly IndicatorRegion[],
  annotationsByRegionId: Record<string, Annotation[]>
): IndicatorPane[] {
  const notesByScroller = new Map<HTMLElement, MeasuredNote[]>();
  regions.forEach(({regionId, container, getProjection}) => {
    const noted = filterNotedAnnotations(annotationsByRegionId[regionId] ?? []);
    const scroller = noted.length > 0 ? resolveIndicatorScroller(container) : null;
    const projection = scroller ? getProjection() : null;
    if (!scroller || !projection) {
      return;
    }
    const measured = measureNotes(noted, projection, scroller);
    if (measured.length > 0) {
      notesByScroller.set(scroller, [...(notesByScroller.get(scroller) ?? []), ...measured]);
    }
  });
  return [...notesByScroller].map(([scroller, notes]) => ({
    scroller,
    lines: groupNotesByLine(notes)
  }));
}

// Keyed by pane, not index: an index key remounts a surviving pane's open peek.
const paneKeys = new WeakMap<HTMLElement, string>();
let mintedPaneKeys = 0;

function paneKeyFor(scroller: HTMLElement): string {
  let key = paneKeys.get(scroller);
  if (!key) {
    mintedPaneKeys += 1;
    key = `note-indicator-pane-${mintedPaneKeys}`;
    paneKeys.set(scroller, key);
  }
  return key;
}

/**
 * Every mounted region's noted marks, flagged by buttons in the margin of the
 * pane that scrolls each region. One layer reads all regions, so notes sharing a
 * line merge into one indicator even across regions — a table row's cells sit on
 * one visual line. The buttons portal into their pane and position absolutely.
 */
export default React.memo(function AnnotationNoteIndicators() {
  const regions = useAnnotationModeStore((state) => state.indicatorRegions);
  const annotationsByRegionId = useAnnotationModeStore((state) => state.annotationsByRegionId);
  const areAnnotationsHidden = useAnnotationModeStore((state) => state.areAnnotationsHidden);
  // The DOM moving is an input React can't see: a bump re-renders to re-measure,
  // and memo() keeps parent renders from paying for the measurement. Do not opt
  // this file into the React Compiler — it would memoize the measurement away.
  const [, onLayoutShift] = useReducer((count: number) => count + 1, 0);
  useOnRegionsShift(regions, onLayoutShift);

  if (areAnnotationsHidden) {
    return null;
  }

  const panes = measureIndicatorPanes(regions, annotationsByRegionId);

  return panes.map(({scroller, lines}) =>
    createPortal(
      lines.map((line) => <AnnotationNoteIndicator key={line.annotations[0].id} line={line} />),
      scroller,
      paneKeyFor(scroller)
    )
  );
});
